import React, { useRef, useEffect } from "react"

import { applyVideoCloudinaryTransforms } from "utils/applyCloudinaryTransforms"
import cloudinaryImagePerDevice from "utils/cloudinaryImagePerDevice"

import Icon from "icons"
import GeneralCard from "../base"
import {
  PodcastCategory,
  BottomRight,
  DesktopWrapper,
  MobileWrapper,
  DesktopCard,
  MobileCard,
  ImageRegular,
  ImageCropped,
  Video,
  Content,
  StyledHeadline,
  Category,
  Body,
  Play,
  PlayIcon,
} from "./styles"

import { BylineAuthor } from "../base/styles"
import PodcastSquareCard from "./square-card"

const PodcastCard = ({
  author = {},
  durationInMinutes,
  hideOnPageLoad,
  video,
  isHomePage,
  isScrollable,
  ...props
}) => {
  const refCardDesktop = useRef(null)
  const refCardMobile = useRef(null)
  const refImage = useRef(null)
  const refVideo = useRef(null)

  if (isScrollable) {
    return <PodcastSquareCard refImage={refImage} props={props} />
  }

  useEffect(() => {
    const handlePlay = () => {
      if (refImage.current && refVideo.current) {
        refImage.current.style.opacity = 0
        refVideo.current.play()
      }
    }

    const handlePause = () => {
      if (refImage.current && refVideo.current) {
        refImage.current.style.opacity = 1
        refVideo.current.pause()
      }
    }

    if (refCardDesktop.current) {
      refCardDesktop.current.addEventListener("mouseenter", handlePlay)
      refCardDesktop.current.addEventListener("mouseleave", handlePause)
    }

    return () => {
      if (refCardDesktop.current) {
        refCardDesktop.current.removeEventListener("mouseenter", handlePlay)
        refCardDesktop.current.removeEventListener("mouseleave", handlePause)
      }
    }
  }, [])

  const mediaWidthDesktop = 661
  const mediaHeightDesktop = 555
  const mediaWidthMobile = 390
  const mediaHeightMobile = 140
  const images = props.image && cloudinaryImagePerDevice(props.image)
  const imageElement = props.image ? (
    <ImageRegular
      elRef={refImage}
      variant="4col"
      baseUrl={images.mobile}
      alt={props.headline}
      images={[
        {
          screenSize: 576,
          url: images.desktop,
          transforms: `g_auto,c_fill,w_${mediaWidthDesktop},h_${mediaHeightDesktop},q_90,fl_lossy,f_auto`,
        },
        {
          url: images.mobile,
          transforms: `g_auto,c_fill,g_auto,w_${mediaWidthMobile},h_${mediaHeightMobile},q_90,f_auto,fl_lossy`,
        },
      ]}
      lazy
      isGrid
      imagePosition="top"
    />
  ) : props.imageCrops ? (
    <ImageCropped
      elRef={refImage}
      variant="4col"
      imageData={props.imageCrops}
      alt={props.headline}
      sizes={[
        {
          query: 576,
          width: mediaWidth,
          height: mediaHeight,
          transforms: `g_auto,c_fill,g_auto,w_${mediaWidthDesktop},h_${mediaHeightDesktop},q_90,f_auto,fl_lossy`,
        },
        {
          query: 0,
          width: mediaWidth,
          height: mediaHeight,
          transforms: `g_auto,c_fill,g_auto,w_${mediaWidthMobile},h_${mediaHeightMobile},q_90,f_auto,fl_lossy`,
        },
      ]}
      lazy
      isGrid
      imagePosition="top"
    />
  ) : null

  const videoElement = video && (
    <Video
      ref={refVideo}
      width="100%"
      muted={video.muteAudio}
      loop={video.loopVideo}
      playsInline
      type="video/mp4"
      src={applyVideoCloudinaryTransforms(
        video.videoObject[0].secure_url,
        `ac_none,c_fill,w_${mediaWidthDesktop},h_${mediaHeightDesktop}`,
      )}
    />
  )

  return videoElement ? (
    <>
      <DesktopWrapper>
        <DesktopCard
          {...props}
          elRef={refCardDesktop}
          hideOnPageLoad={hideOnPageLoad}
          headline={props.headline}
          headlineOverride={
            <Content>
              <StyledHeadline variant="xlarge">{props.headline} →</StyledHeadline>
              <Category>{props.category}</Category>
              <Body dangerouslySetInnerHTML={{ __html: props.subheadline }} />
              <Play>
                <PlayIcon id="play-icon-empty" width={26} height={27} />
                &nbsp;&nbsp;+100 episodes
              </Play>
            </Content>
          }
          subheadline=""
          imageOverride={imageElement}
          video={videoElement}
        />
      </DesktopWrapper>
      <MobileWrapper>
        <MobileCard
          {...props}
          elRef={refCardMobile}
          hideOnPageLoad={hideOnPageLoad}
          headline={props.headline}
          headlineOverride={
            <Content>
              <StyledHeadline variant="xlarge">{props.headline} →</StyledHeadline>
              <Category>{props.category}</Category>
              <Body dangerouslySetInnerHTML={{ __html: props.subheadline }} />
              <Play>
                <PlayIcon id="play-icon-empty" width={26} height={27} />
                &nbsp;&nbsp;+100 episodes
              </Play>
            </Content>
          }
          subheadline=""
          imageOverride={imageElement}
        />
      </MobileWrapper>
    </>
  ) : (
    <GeneralCard
      {...props}
      hideOnPageLoad={hideOnPageLoad}
      topLeftElement={<PodcastCategory>{durationInMinutes} min podcast</PodcastCategory>}
      bylineElement={
        author.url ? (
          <a href={`/wc/${author.url}`} title={author.name}>
            <BylineAuthor>{author.name}</BylineAuthor>
          </a>
        ) : (
          <BylineAuthor>{author.name}</BylineAuthor>
        )
      }
      bottomRightElement={
        <BottomRight>
          <Icon id="media-headphones-icon" width={36} height={36} />
        </BottomRight>
      }
    />
  )
}

export default PodcastCard
